"use client";

import type { ReactNode } from "react";
import { useRef, useState } from "react";
import { useMobileLayout } from "@/hooks/use-mobile-layout";
import { LONG_PRESS_MENU_MS } from "@/desk/hooks/use-long-press";
import { StudioActionMenu } from "@/studio/components/chrome/StudioActionMenu";
import type { StudioMenuItem } from "./types";

/** Right-click on desktop. Long-press opens the same items as a phone sheet. */
export function StudioContextMenu({
  label,
  items,
  children,
  veil = true,
}: {
  label: string;
  items: StudioMenuItem[];
  children: ReactNode;
  /** Full-card page frost. Run leaves this off. */
  veil?: boolean;
}) {
  const { isMobile } = useMobileLayout();
  const [point, setPoint] = useState<{ x: number; y: number } | null>(null);
  const suppressClickRef = useRef(false);

  return (
    <>
      <div
        style={{ width: "100%" }}
        onContextMenu={(event) => {
          // Phone: the hold timer owns the menu. Still cancel Android's native
          // long-press menu so it does not open a second menu on top.
          if (isMobile) {
            event.preventDefault();
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          setPoint({ x: event.clientX, y: event.clientY });
        }}
        onPointerDown={(event) => {
          if (!isMobile || event.button !== 0) return;
          const target = event.currentTarget;
          const x = event.clientX;
          const y = event.clientY;
          const timer = window.setTimeout(() => {
            suppressClickRef.current = true;
            setPoint({ x, y });
          }, LONG_PRESS_MENU_MS);
          const clear = () => window.clearTimeout(timer);
          target.addEventListener("pointerup", clear, { once: true });
          target.addEventListener("pointerleave", clear, { once: true });
          target.addEventListener("pointercancel", clear, { once: true });
        }}
        onClickCapture={(event) => {
          if (!suppressClickRef.current) return;
          suppressClickRef.current = false;
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        {children}
      </div>
      {point ? (
        <StudioActionMenu
          label={label}
          point={point}
          veil={veil}
          onClose={() => setPoint(null)}
          items={items.map((item) => ({
            id: item.id,
            label: item.label,
            icon: item.icon,
            danger: item.danger,
            section: "actions",
            onSelect: item.onSelect,
          }))}
        />
      ) : null}
    </>
  );
}
